import { Alert, Button, Flex, Grid, notify } from 'front-commons/ds';
import { Icon, Typography } from 'hype-ds';
import { useEffect, useRef, useState } from 'react';
import { BasketFullParams } from 'services/basket/interfaces';
import useBasket from 'stores/basket';
import useDrawer from 'stores/drawer';
import DistributorCard from './components/DistributorCard';
import useCustomer from 'stores/customer';
import usePos from 'stores/pos';
import CartTotals from './components/CartTotals';
import useDialog from 'stores/dialog';
import { getBasketSuggestion } from 'services/basket';
import { useNavigate } from 'react-router-dom';
import { useShouldShowAlert } from 'hooks/useShouldShowAlert';
import { BoniBanner } from './components/BannerBoni';
import { roleAlertConfig } from './helpers';
import useBoniVisibility from 'containers/Boni/hooks/useBoniVisibility';
import { BoniPoints } from './components/BoniPoints/index.';
import { onViewBoniPoints } from 'shared/gtm';

export default function SideBasketDrawer() {
	const navigate = useNavigate();
	const [loadingSuggestions, setLoadingSuggestions] = useState(false);
	const hasFiredViewBoniPoints = useRef(false);

	const { handleOpenDialog } = useDialog();
	const {
		handleHasPermission,
		customerStore: {
			data: { hyperaRole, id: customerId },
		},
	} = useCustomer();

	const isSupervisor = hyperaRole === 'SUPERVISOR';

	const { handleCloseDrawer, handleOpenDrawer } = useDrawer();
	const {
		posStore: {
			selectedPos: { id: posId },
		},
	} = usePos();
	const {
		basketStore: {
			basket: { baskets, discount, totalTaxSubstitution, totalPrice, finalTotalPrice },
			loading: loadingBasketType,
			deleting: deletingProductId,
		},
		basketCheckerHandler,
		handleGetBasketData,
		handleRemoveFromBasket,
	} = useBasket();

	const totalLiquid = totalPrice - discount;

	const { showBoniPoints, levelName } = useBoniVisibility(totalLiquid);

	const hasEditBasketPermission = handleHasPermission('BASKET_EDIT');

	const groupedByDistributors = Object.entries(
		baskets.reduce((acc, item) => {
			if (acc[item.distributorName]) acc[item.distributorName].push(item);
			if (!acc[item.distributorName]) acc[item.distributorName] = [item];

			return acc;
		}, {} as Record<string, BasketFullParams[]>),
	);

	const isEmpty = !groupedByDistributors.length;
	const skeletonQuantity = loadingBasketType === 'full' ? groupedByDistributors.length || 1 : 0;

	const handleCloseBasketDrawer = () => handleCloseDrawer('side-basket-drawer');

	const handleCleanCartClick = () => {
		handleOpenDialog({
			heading: { title: 'Limpar carrinho', showCloseButton: true },
			content: { description: 'Deseja realmente limpar o carrinho?' },
			footer: {
				primaryButton: { label: 'Não' },
				secondaryButton: {
					label: 'Sim',
					onClick: () => basketCheckerHandler.handleClearSelectedBaskets(true),
				},
			},
		});
	};

	const handleOrderRevisionClick = async () => {
		setLoadingSuggestions(true);

		try {
			const response = await getBasketSuggestion(posId, navigate);
			if ([200].includes(response.code)) {
				handleCloseBasketDrawer();
			}
			if ([204].includes(response.status)) {
				handleCloseBasketDrawer();
				navigate(`/revisar-pedido`);
			}
		} catch (err) {
			if (err === 409) {
				handleCloseBasketDrawer();
				navigate(`/revisar-pedido`);
			} else {
				notify.negative({ description: 'Ocorreu um erro ao revisar seu pedido.' });
			}
		} finally {
			setLoadingSuggestions(false);
		}
	};

	const alertConfig = hyperaRole ? roleAlertConfig[hyperaRole] : roleAlertConfig.DEFAULT;

	const { showAlert } = useShouldShowAlert({
		alertKey: alertConfig.alertKey,
		config: {
			id: alertConfig.id(customerId),
			alertDays: alertConfig.alertDays,
		},
	});

	useEffect(() => {
		handleGetBasketData({ loading: 'full' });

		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = 'auto';
		};
	}, []);

	useEffect(() => {
		if (loadingBasketType || !showBoniPoints) return;
		if (hasFiredViewBoniPoints.current) return;

		hasFiredViewBoniPoints.current = true;

		onViewBoniPoints({
			basketProductsData: baskets?.map((basket) =>
				basket.products.map((product) => ({
					...product,
					affiliation: basket.distributorName,
					businessUnitName: basket.businessUnitName,
					industryName: basket.industryName,
				})),
			),
			totalPricePromotion: finalTotalPrice,
			levelName,
		});
	}, [loadingBasketType, showBoniPoints]);

	return (
		<Flex
			gap="0px"
			width="100%"
			height="100%"
			padding="16px 8px 16px 16px !important"
			direction="column"
			data-testid="side-basket"
		>
			<Flex direction="column" gap="16px" padding="0 8px 16px 0">
				<Flex as="header" alignItems="center" data-testid="side-basket-Header" justifyContent="space-between">
					<Typography.Label.M color="--text-primary">Itens no carrinho</Typography.Label.M>

					<Button
						variant="none"
						onClick={() => handleCloseDrawer('side-basket-drawer')}
						leftIcon={{ name: 'close', size: '24px', weight: 500, color: '--text-link' }}
					/>
				</Flex>

				{showAlert && (
					<Alert type="info" toastMode preserveMobileRadius>
						<>
							Agora os pedidos do {isSupervisor ? 'supervisor' : 'consultor'} e do PDV são feitos em carrinhos
							separados. Este carrinho é exclusivo para você,{' '}
							<Typography.Label.M>{isSupervisor ? 'Supervisor Hypera' : 'Consultor Hypera'}</Typography.Label.M>!
						</>
					</Alert>
				)}
			</Flex>

			<Flex
				as="section"
				gap={{ small: '16px', medium: '24px' }}
				height="100%"
				padding="0 8px 24px 0"
				overflow="auto"
				direction="column"
				data-testid="side-basket-Content"
			>
				<Flex width="100%">
					<BoniBanner variant="default" />
				</Flex>

				{Array.generate(skeletonQuantity).map((index) => (
					<DistributorCard.Skeleton key={index} />
				))}

				{isEmpty && !skeletonQuantity && (
					<Flex direction="column" alignItems="center" gap="8px" width="100%" maxWidth="335px" margin="48px auto 0">
						<Icon name="shopping_cart" size="48px" color="--text-secondary" />

						<Typography.Heading.XS textAlign="center" color="--text-primary">
							Seu carrinho está vazio
						</Typography.Heading.XS>

						<Typography.Paragraph.M textAlign="center" color="--text-primary">
							Você pode navegar pelo menu ou utilizar a busca para encontrar o que deseja.
						</Typography.Paragraph.M>
					</Flex>
				)}

				{!skeletonQuantity &&
					groupedByDistributors.map(([distributorName, group], index) => (
						<DistributorCard
							key={distributorName}
							name={distributorName}
							posId={posId}
							sources={group}
							openIndex={index}
							handleOpenDialog={handleOpenDialog}
							handleOpenDrawer={handleOpenDrawer}
							loadingBasketType={loadingBasketType}
							deletingProductId={deletingProductId}
							handleRemoveFromBasket={handleRemoveFromBasket}
							hasEditBasketPermission={hasEditBasketPermission}
						/>
					))}
			</Flex>

			{!isEmpty && !skeletonQuantity && (
				<Flex
					as="footer"
					gap="16px"
					boxShadow="0px -4px 24px 0px #00000029"
					width="calc(100% + 24px)"
					height="fit-content"
					margin="0 -16px -16px"
					padding="16px"
					direction="column"
					data-testid="side-basket-Footer"
				>
					<Flex gap="8px" direction="column">
						<CartTotals tax={totalTaxSubstitution} brute={totalPrice} liquid={finalTotalPrice} discount={discount} />

						{showBoniPoints && (
							<BoniPoints baskets={baskets} finalTotalPrice={finalTotalPrice} totalLiquid={totalLiquid} />
						)}
					</Flex>

					<Alert type="note">
						<Typography.Paragraph.S>
							Os preços poderão sofrer alteração por impostos praticados pelos distribuidores
						</Typography.Paragraph.S>
					</Alert>

					<Grid gap="16px" columns="1fr 1fr">
						<Button
							width="100%"
							variant="secondary"
							onClick={handleCleanCartClick}
							disabled={loadingSuggestions || !!loadingBasketType}
							hasPermission={hasEditBasketPermission}
						>
							Limpar carrinho
						</Button>

						<Button
							width="100%"
							onClick={handleOrderRevisionClick}
							loading={loadingSuggestions}
							disabled={!!loadingBasketType}
						>
							Revisar pedido
						</Button>
					</Grid>
				</Flex>
			)}
		</Flex>
	);
}
